import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../integrations/supabase/client';
import type { PosizioneCampo } from '../integrations/supabase/types';

const POLL_INTERVAL_MS = 60_000;
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string) || 'https://nzyekwbrbjdueabxhzjf.supabase.co';
const SUPABASE_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || 'sb_publishable_wem306c6HQ-nQKZLkTkvSg_lM8tg8po';

async function fetchDiretto(codice: string): Promise<{ data: PosizioneCampo[] | null; error: string | null }> {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/posizioni_campo?select=*&codice_operazione=eq.${encodeURIComponent(codice)}`,
      { headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` } }
    );
    if (!res.ok) return { data: null, error: `HTTP ${res.status}` };
    const data = await res.json();
    return { data: data as PosizioneCampo[], error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : String(e) };
  }
}

export function usePosizioniCampo(codiceOperazione: string | null) {
  const [posizioni, setPosizioni] = useState<PosizioneCampo[]>([]);
  const [connesso, setConnesso] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchPosizioni = useCallback(async () => {
    if (!codiceOperazione) return;
    const { data, error } = await fetchDiretto(codiceOperazione);
    if (error) {
      setFetchError(error);
    } else {
      setFetchError(null);
      const soglia = Date.now() - 60 * 60 * 1000; // 60 minuti
      const attivi = (data ?? []).filter(
        (p) => p.stato !== 'rientro' && new Date(p.updated_at).getTime() > soglia
      );
      setPosizioni(attivi);
    }
  }, [codiceOperazione]);

  useEffect(() => {
    if (!codiceOperazione) return;

    fetchPosizioni();
    pollRef.current = setInterval(fetchPosizioni, POLL_INTERVAL_MS);

    // Realtime via Supabase client (fallback al polling se non funziona)
    const channel = supabase
      .channel(`campo:${codiceOperazione}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'posizioni_campo', filter: `codice_operazione=eq.${codiceOperazione}` },
        () => { fetchPosizioni(); }
      )
      .subscribe((status) => setConnesso(status === 'SUBSCRIBED'));

    channelRef.current = channel;
    return () => {
      channel.unsubscribe();
      setConnesso(false);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [codiceOperazione, fetchPosizioni]);

  return { posizioni, connesso, fetchError, ricarica: fetchPosizioni };
}
